// This is used as the API slice definition and acts as our network API interface layer.
import { createApi, BaseQueryFn } from '@reduxjs/toolkit/query/react';
import { AxiosRequestConfig, AxiosError } from 'axios';
import { API } from '../../utils/AmplifyApiUtils';
import { PortalConfigEntity } from '../../constants';
import { handleNativeHttpRequestFetcher } from '../../utils/httpUtils';

export enum ApiTags {
  domains = 'Domains',
  domains_emails = 'Domain Emails',
  recommended_resources = 'Recommended Resources',
  latest_releases = 'Latest Releases',
  notifications_settings = 'Notifications Settings',
  payment_fees = 'Payment Fees',
  referrals = 'Referrals',
  importer = 'Importer',
  forms = 'Forms',
  form_responses = 'Form Responses',
  templates = 'Templates',
  inbox_notifications = 'Inbox Notifications',
}
export const amplifyBaseQuery = (
  { apiName }: { apiName: string } = { apiName: 'AppAPI' },
): BaseQueryFn<
  {
    path: string;
    method: AxiosRequestConfig['method'];
    options: {
      [key: string]: any;
    };
  },
  unknown,
  unknown
> => async ({ path, method, options }) => {
  const jestEnv = process.env.JEST_WORKER_ID;
  // if it is jest env then use fetch instead of aws-amplify api client
  if (jestEnv) {
    const result = handleNativeHttpRequestFetcher(
      path,
      method,
      undefined,
      undefined,
    );
    return { data: result };
  }

  // reach this code only on non-jest env
  try {
    switch (method) {
      case 'get': {
        const result = await API.get(apiName, path, options);
        return { data: result };
      }
      case 'post': {
        const result = await API.post(apiName, path, options);
        return { data: result };
      }
      case 'del': {
        const result = await API.del(apiName, path, options);
        return { data: result };
      }
      case 'put': {
        const result = await API.put(apiName, path, options);
        return { data: result };
      }
      default:
        return { error: 'Unsupported method' };
    }
  } catch (axiosError) {
    const err = axiosError as AxiosError;
    return {
      error: { status: err.response?.status, data: err.response?.data },
    };
  }
};

export const appAPI = createApi({
  baseQuery: amplifyBaseQuery({ apiName: 'AppAPI' }),
  reducerPath: 'appAPI',
  tagTypes: [
    ApiTags.domains,
    ApiTags.domains_emails,
    ApiTags.recommended_resources,
    ApiTags.latest_releases,
    ApiTags.notifications_settings,
    ApiTags.payment_fees,
    ApiTags.referrals,
    ApiTags.importer,
    ApiTags.forms,
    ApiTags.form_responses,
    ApiTags.templates,
    ApiTags.inbox_notifications,
  ],
  endpoints: build => ({
    getPortalConfig: build.query<PortalConfigEntity, void>({
      query: () => ({ path: '/entities/config', method: 'get', options: {} }),
    }),
    savePortalConfig: build.mutation<
      PortalConfigEntity,
      Partial<PortalConfigEntity> & Pick<PortalConfigEntity, 'id'>
    >({
      query: body => ({
        path: '/entities/config',
        method: 'post',
        options: {
          body,
        },
      }),
    }),
  }),
});

export const {
  useGetPortalConfigQuery,
  useSavePortalConfigMutation,
  endpoints,
} = appAPI;
